import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Header from "@/components/Header";
import ProductCard, { formatPrice } from "@/components/ProductCard";
import { useAuth } from "@/context/AuthContext";
import { useCart, addRecentlyViewed } from "@/context/CartContext";
import { useFavorites } from "@/context/FavoritesContext";
import AgeGate, { isAgeVerified } from "@/components/AgeGate";
import { toast } from "sonner";
import {
  Star, ShieldCheck, CreditCard, Lightning, Heart, Storefront,
  ArrowUUpLeft, ChatCircleDots, ChatCenteredText, SealCheck, Truck, Warning,
} from "@phosphor-icons/react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1495100497150-fe209c585f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

function Stars({ value = 0, size = 16 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} weight={i <= Math.round(value) ? "fill" : "regular"} size={size}
              className={i <= Math.round(value) ? "text-primary" : "text-gray-300"} />
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { add } = useCart();
  const { isFav, toggle: toggleFav } = useFavorites();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [related, setRelated] = useState([]);
  const [reviewData, setReviewData] = useState({ reviews: [], average: 0, count: 0, distribution: {} });
  const [questions, setQuestions] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [ageOk, setAgeOk] = useState(isAgeVerified());

  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [newPhotos, setNewPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [answerText, setAnswerText] = useState({});

  const questionsRef = useRef();

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, r, rev, q] = await Promise.all([
        api.get(`/products/${id}`),
        api.get(`/products/${id}/related`),
        api.get(`/products/${id}/reviews`),
        api.get(`/products/${id}/questions`),
      ]);
      setProduct(p.data);
      setRelated(r.data);
      setReviewData(rev.data);
      setQuestions(q.data);
      setActiveImg(0);
      setSelectedVariant(null);
      addRecentlyViewed(id);
    } catch (e) {
      toast.error("Producto no encontrado");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); window.scrollTo(0, 0); /* eslint-disable-next-line */ }, [id]);

  const handleBuy = async () => {
    if (hasVariants && !selectedVariant) { toast.error("Selecciona una variante"); return; }
    add(id, selectedVariant?.id || null, 1);
    navigate("/carrito");
  };

  const addToCart = () => {
    if (hasVariants && !selectedVariant) { toast.error("Selecciona una variante"); return; }
    add(id, selectedVariant?.id || null, 1);
    toast.success("Agregado al carrito");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { login(); return; }
    if (newRating < 1) { toast.error("Selecciona una calificación"); return; }
    try {
      await api.post(`/products/${id}/reviews`, { rating: newRating, comment: newComment, photos: newPhotos });
      toast.success("¡Gracias por tu opinión!");
      setNewRating(0); setNewComment(""); setNewPhotos([]);
      setReviewData((await api.get(`/products/${id}/reviews`)).data);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo enviar la opinión"); }
  };

  const uploadReviewPhotos = async (files) => {
    const arr = Array.from(files).slice(0, 5 - newPhotos.length);
    if (!arr.length) return;
    setUploadingPhoto(true);
    try {
      const paths = [];
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload/review", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(r.data.path);
      }
      setNewPhotos((p) => [...p, ...paths]);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo subir la foto"); }
    finally { setUploadingPhoto(false); }
  };

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!user) { login(); return; }
    if (!newQuestion.trim()) return;
    try {
      await api.post(`/products/${id}/questions`, { text: newQuestion });
      toast.success("Pregunta enviada");
      setNewQuestion("");
      setQuestions((await api.get(`/products/${id}/questions`)).data);
    } catch (e) { toast.error("No se pudo enviar la pregunta"); }
  };

  const submitAnswer = async (qid) => {
    const text = (answerText[qid] || "").trim();
    if (!text) return;
    try {
      await api.post(`/questions/${qid}/answer`, { text });
      setAnswerText((s) => ({ ...s, [qid]: "" }));
      setQuestions((await api.get(`/products/${id}/questions`)).data);
      toast.success("Respuesta publicada");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo responder"); }
  };

  if (loading) return <div className="App min-h-screen"><Header /><div className="max-w-[1200px] mx-auto p-8"><div className="h-96 bg-white rounded-lg animate-pulse" /></div></div>;
  if (!product) return <div className="App min-h-screen"><Header /><div className="max-w-[1200px] mx-auto p-8">Producto no encontrado</div></div>;

  const mainPath = product.main_image;
  const orderedPaths = mainPath ? [mainPath, ...(product.images || []).filter((p) => p !== mainPath)] : (product.images || []);
  const images = orderedPaths.length ? orderedPaths.map(fileUrl) : [PLACEHOLDER];
  const specs = product.specs || [];
  const bullets = specs.slice(0, 3);
  const maxDist = Math.max(1, ...Object.values(reviewData.distribution || {}));
  const hasVariants = product.variants && product.variants.length > 0;
  const displayPrice = selectedVariant?.price ?? product.price;
  const currentStock = hasVariants ? (selectedVariant ? selectedVariant.stock : product.total_stock) : product.stock;
  const soldOut = product.is_sold_out || product.total_stock === 0;
  const isAdmin = user?.is_admin;

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      {product.is_adult && !ageOk && <AgeGate onAccept={() => setAgeOk(true)} />}
      <div className="max-w-[1200px] mx-auto px-3 sm:px-5 py-3">
        <div className="flex items-center gap-2 text-[13px] text-primary mb-3 flex-wrap">
          <button onClick={() => navigate(-1)} data-testid="back-button" className="flex items-center gap-1 hover:underline">
            <ArrowUUpLeft weight="bold" size={14} /> Volver
          </button>
          {(product.category_names || []).map((c) => (
            <React.Fragment key={c}><span className="text-muted-foreground">›</span><span className="text-muted-foreground">{c}</span></React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-lg nb-shadow p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Gallery */}
            <div className="lg:col-span-5">
              <div className="flex gap-3">
                <div className="flex flex-col gap-2">
                  {images.map((im, i) => (
                    <button key={i} onMouseEnter={() => setActiveImg(i)} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                            className={`w-12 h-12 rounded-md border overflow-hidden ${activeImg === i ? "border-primary ring-1 ring-primary" : "border-black/10"}`}>
                      <img src={im} alt="" className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.src=PLACEHOLDER;}} />
                    </button>
                  ))}
                </div>
                <div className="flex-1 rounded-lg overflow-hidden bg-white flex items-center justify-center aspect-square border border-black/5">
                  <img src={images[activeImg]} alt={product.title} className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.src=PLACEHOLDER;}} />
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="lg:col-span-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{product.condition || "Nuevo"} · +{product.sold_count || 0} vendidos</span>
                <button onClick={() => toggleFav(product.id)} data-testid="favorite-button" className={isFav(product.id) ? "text-red-500" : "text-muted-foreground hover:text-red-500"}>
                  <Heart weight={isFav(product.id) ? "fill" : "regular"} size={22} />
                </button>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight mt-1 leading-snug" data-testid="product-title">{product.is_adult && <span className="align-middle text-[11px] font-bold bg-red-600 text-white rounded px-1.5 py-0.5 mr-2">+18</span>}{product.title}</h1>

              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-muted-foreground">{reviewData.average || 0}</span>
                <Stars value={reviewData.average} size={15} />
                <span className="text-sm text-muted-foreground">({reviewData.count})</span>
              </div>

              <div className="mt-4">
                {product.compare_at_price > displayPrice && (
                  <div className="text-base text-muted-foreground line-through">{formatPrice(product.compare_at_price)}</div>
                )}
                <div className="text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3" data-testid="product-price">
                  {formatPrice(displayPrice)}
                  {product.discount_percent > 0 && <span className="text-base font-semibold text-[hsl(var(--meli-green))]">{product.discount_percent}% OFF</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">en 3 MSI de {formatPrice(displayPrice / 3)} con tarjeta de crédito</p>
              </div>

              {/* Variants */}
              {hasVariants && (
                <div className="mt-5" data-testid="variant-selector">
                  <p className="font-semibold text-sm mb-2">Opciones disponibles: {selectedVariant && <span className="text-primary">{selectedVariant.name}</span>}</p>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((v) => {
                      const out = v.stock < 1;
                      const active = selectedVariant?.id === v.id;
                      return (
                        <button key={v.id} disabled={out} onClick={() => { setSelectedVariant(v); if (v.image) { const idx = orderedPaths.indexOf(v.image); if (idx >= 0) setActiveImg(idx); } }} data-testid={`variant-${v.id}`}
                                className={`px-3 py-1.5 rounded-md border text-sm transition-all ${active ? "border-primary ring-1 ring-primary text-primary font-semibold" : "border-black/15 hover:border-primary"} ${out ? "opacity-40 line-through cursor-not-allowed" : ""}`}>
                          {v.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {bullets.length > 0 && (
                <div className="mt-5">
                  <p className="font-semibold text-sm mb-2">Lo que tienes que saber</p>
                  <ul className="space-y-1.5">
                    {bullets.map((b, i) => (
                      <li key={i} className="text-sm text-foreground/80 flex gap-2"><span className="text-muted-foreground">•</span><span><span className="text-muted-foreground">{b.label}:</span> {b.value}</span></li>
                    ))}
                  </ul>
                  <a href="#caracteristicas" className="text-[13px] text-primary mt-2 inline-block hover:underline">Ver características</a>
                </div>
              )}
            </div>

            {/* Buy box */}
            <div className="lg:col-span-3">
              <div className="border border-black/10 rounded-lg p-4 lg:sticky lg:top-24">
                {product.free_shipping ? (
                  <p className="text-sm font-semibold text-[hsl(var(--meli-green))] flex items-center gap-1"><Truck weight="bold" size={15} /> Envío gratis a todo el país</p>
                ) : product.shipping_cost != null ? (
                  <p className="text-sm font-semibold text-foreground/80 flex items-center gap-1"><Truck weight="bold" size={15} className="text-muted-foreground" /> Envío: {formatPrice(product.shipping_cost)}</p>
                ) : (
                  <p className="text-sm text-muted-foreground flex items-center gap-1"><Truck weight="bold" size={15} /> Costo de envío calculado en el carrito</p>
                )}
                <p className="text-sm font-semibold mt-4">{soldOut ? "Sin stock" : "Stock disponible"}</p>
                {!soldOut && (
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <Lightning weight="fill" size={12} className="text-[hsl(var(--meli-green))]" />
                    <span className="font-bold text-[hsl(var(--meli-green))]">Envío Súper Rápido</span>
                    {(!hasVariants || selectedVariant) && ` · (${currentStock} disp.)`}
                  </p>
                )}
                {product.is_adult && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-3 flex items-start gap-1.5" data-testid="adult-no-warranty">
                    <Warning weight="fill" size={14} className="shrink-0 mt-0.5" /> Producto para adultos (+18). No incluye garantía.
                  </p>
                )}

                <div className="mt-4 space-y-2">
                  <button onClick={handleBuy} disabled={soldOut} data-testid="buy-product-button"
                          className="w-full nb-btn bg-primary text-primary-foreground py-2.5 hover:brightness-110 disabled:opacity-50">
                    {soldOut ? "Sin stock" : "Comprar ahora"}
                  </button>
                  <button onClick={addToCart} disabled={soldOut} data-testid="add-to-cart-button"
                          className="w-full nb-btn bg-primary/10 text-primary py-2.5 hover:bg-primary/20 disabled:opacity-50">
                    Agregar al carrito
                  </button>
                  <button onClick={() => questionsRef.current?.scrollIntoView({ behavior: "smooth" })} data-testid="ask-seller-button"
                          className="w-full text-sm text-primary py-1 hover:underline">
                    Preguntar
                  </button>
                </div>

                <div className="mt-4 pt-4 border-t border-black/10 text-sm">
                  <p className="text-muted-foreground text-xs">Vendido por</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center"><Storefront weight="bold" size={15} /></span>
                    <div>
                      <p className="font-semibold text-primary leading-tight">Todito Ventas</p>
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1"><SealCheck weight="fill" size={12} className="text-primary" /> Tienda oficial</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2.5 text-xs text-muted-foreground">
                  <p className="flex items-start gap-2"><ArrowUUpLeft weight="bold" size={15} className="text-primary shrink-0 mt-0.5" /> <span><span className="text-primary">Devolución gratis.</span> 30 días desde que lo recibes.</span></p>
                  <p className="flex items-start gap-2"><ShieldCheck weight="bold" size={15} className="text-primary shrink-0 mt-0.5" /> <span><span className="text-primary">Compra Protegida.</span> Recibe el producto o te devolvemos tu dinero.</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="bg-white rounded-lg nb-shadow p-4 sm:p-6 mt-4">
            <h2 className="text-lg font-bold tracking-tight mb-4">Productos relacionados</h2>
            <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2" data-testid="related-products">
              {related.map((p) => (<div key={p.id} className="w-44 sm:w-52 shrink-0"><ProductCard product={p} /></div>))}
            </div>
          </div>
        )}

        {/* Características */}
        <div id="caracteristicas" className="bg-white rounded-lg nb-shadow p-4 sm:p-6 mt-4">
          <h2 className="text-lg font-bold tracking-tight mb-4">Características del producto</h2>
          <div className="rounded-lg overflow-hidden border border-black/10" data-testid="specs-table">
            <table className="w-full text-sm">
              <tbody>
                <tr className="odd:bg-secondary/50"><td className="p-3 text-muted-foreground w-1/3">Condición</td><td className="p-3">{product.condition || "Nuevo"}</td></tr>
                {product.sku && <tr className="odd:bg-secondary/50"><td className="p-3 text-muted-foreground">SKU</td><td className="p-3">{product.sku}</td></tr>}
                {(product.category_names || []).length > 0 && <tr className="odd:bg-secondary/50"><td className="p-3 text-muted-foreground">Categoría</td><td className="p-3">{product.category_names.join(", ")}</td></tr>}
                {specs.map((s, i) => (<tr key={i} className="odd:bg-secondary/50"><td className="p-3 text-muted-foreground">{s.label}</td><td className="p-3">{s.value}</td></tr>))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Descripción */}
        <div className="bg-white rounded-lg nb-shadow p-4 sm:p-6 mt-4">
          <h2 className="text-lg font-bold tracking-tight mb-4">Descripción</h2>
          <p className="text-[15px] text-foreground/80 leading-relaxed whitespace-pre-line" data-testid="product-description">{product.detailed_description || product.description}</p>
        </div>

        {/* Opiniones */}
        <div className="bg-white rounded-lg nb-shadow p-4 sm:p-6 mt-4">
          <h2 className="text-lg font-bold tracking-tight mb-4">Opiniones del producto</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center md:border-r border-black/10 md:pr-6">
              <div className="text-5xl font-semibold">{reviewData.average || 0}</div>
              <div className="flex justify-center mt-1"><Stars value={reviewData.average} size={16} /></div>
              <p className="text-sm text-muted-foreground mt-1">{reviewData.count} opiniones</p>
            </div>
            <div className="md:col-span-2 flex flex-col justify-center gap-1.5">
              {[5, 4, 3, 2, 1].map((star) => {
                const c = (reviewData.distribution || {})[String(star)] || 0;
                return (
                  <div key={star} className="flex items-center gap-2 text-xs">
                    <span className="w-3 text-muted-foreground">{star}</span>
                    <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(c / maxDist) * 100}%` }} /></div>
                    <span className="w-6 text-right text-muted-foreground">{c}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {user && reviewData.can_review ? (
            <form onSubmit={submitReview} className="mt-6 border-t border-black/10 pt-5" data-testid="review-form">
              <p className="font-semibold text-sm mb-2">{reviewData.already_reviewed ? "Editar tu opinión" : "Deja tu opinión"} <span className="text-[hsl(var(--meli-green))] text-xs font-bold ml-1">✓ Compra verificada</span></p>
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button type="button" key={i} onClick={() => setNewRating(i)} data-testid={`rating-star-${i}`}>
                    <Star weight={i <= newRating ? "fill" : "regular"} size={26} className={i <= newRating ? "text-primary" : "text-gray-300"} />
                  </button>
                ))}
              </div>
              <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} rows={3} placeholder="Cuéntanos tu experiencia…"
                        data-testid="review-comment" className="w-full border border-black/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {newPhotos.map((p, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-md overflow-hidden border border-black/10">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setNewPhotos((s) => s.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 text-white rounded-bl px-1 text-xs">×</button>
                  </div>
                ))}
                {newPhotos.length < 5 && (
                  <label data-testid="review-photo-upload" className="w-16 h-16 rounded-md border border-dashed border-black/25 flex items-center justify-center text-xs text-muted-foreground cursor-pointer hover:border-primary">
                    {uploadingPhoto ? "…" : "+ Foto"}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadReviewPhotos(e.target.files)} />
                  </label>
                )}
              </div>
              <button type="submit" data-testid="submit-review-button" className="mt-3 nb-btn bg-primary text-primary-foreground px-5 py-2 text-sm hover:brightness-110">Publicar opinión</button>
            </form>
          ) : user ? (
            <p className="mt-6 border-t border-black/10 pt-5 text-sm text-muted-foreground" data-testid="review-locked">Solo puedes opinar sobre productos que compraste.</p>
          ) : (
            <button onClick={login} className="mt-6 border-t border-black/10 pt-5 text-sm text-primary hover:underline block">Inicia sesión para opinar</button>
          )}

          <div className="mt-6 space-y-4" data-testid="reviews-list">
            {reviewData.reviews.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay opiniones.</p>}
            {reviewData.reviews.map((r) => (
              <div key={r.id} className="border-b border-black/5 pb-4 last:border-0">
                <div className="flex items-center gap-2">
                  <Stars value={r.rating} size={13} />
                  {r.verified && <span className="text-[10px] font-bold text-[hsl(var(--meli-green))] flex items-center gap-0.5"><SealCheck weight="fill" size={12} /> Compra verificada</span>}
                </div>
                <p className="text-sm text-foreground/85 mt-1.5">{r.comment}</p>
                {(r.photos || []).length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {r.photos.map((p, i) => (
                      <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-md overflow-hidden border border-black/10 block">
                        <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">{r.user_name} · {new Date(r.created_at).toLocaleDateString("es-MX")}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Preguntas */}
        <div ref={questionsRef} className="bg-white rounded-lg nb-shadow p-4 sm:p-6 mt-4 mb-8">
          <h2 className="text-lg font-bold tracking-tight mb-4">Preguntas y respuestas</h2>
          {user ? (
            <form onSubmit={submitQuestion} className="flex gap-2" data-testid="question-form">
              <input value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} placeholder="Escribe tu pregunta…"
                     data-testid="question-input" className="flex-1 border border-black/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              <button type="submit" data-testid="submit-question-button" className="nb-btn bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:brightness-110">Preguntar</button>
            </form>
          ) : (
            <button onClick={login} className="text-sm text-primary hover:underline">Inicia sesión para preguntar</button>
          )}

          <div className="mt-6 space-y-4" data-testid="questions-list">
            {questions.length === 0 && <p className="text-sm text-muted-foreground">Todavía no hay preguntas.</p>}
            {questions.map((q) => (
              <div key={q.id} className="border-b border-black/5 pb-4 last:border-0">
                <div className="flex gap-2 items-start">
                  <ChatCircleDots weight="bold" size={18} className="text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{q.text}</p>
                    <p className="text-[11px] text-muted-foreground">{q.user_name} · {new Date(q.created_at).toLocaleDateString("es-MX")}</p>
                    {q.answer ? (
                      <div className="mt-2 ml-1 pl-3 border-l-2 border-primary/30">
                        <p className="text-sm text-foreground/80 flex gap-2"><ChatCenteredText weight="bold" size={15} className="text-primary shrink-0 mt-0.5" /> {q.answer}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 ml-6">Respuesta de {q.answered_by || "Todito Ventas"}{q.answered_at ? ` · ${new Date(q.answered_at).toLocaleDateString("es-MX")}` : ""}</p>
                      </div>
                    ) : isAdmin ? (
                      <div className="mt-2 flex gap-2">
                        <input value={answerText[q.id] || ""} onChange={(e) => setAnswerText((s) => ({ ...s, [q.id]: e.target.value }))} placeholder="Responder…" data-testid={`answer-input-${q.id}`}
                               className="flex-1 border border-black/10 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        <button onClick={() => submitAnswer(q.id)} data-testid={`answer-button-${q.id}`} className="nb-btn bg-primary text-primary-foreground px-4 py-1.5 text-sm hover:brightness-110">Responder</button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1 italic">Sin responder aún</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
